import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { BrowseFiles, Icon, PrimaryButton, LightButton } from "best-common-react";
import { useGames } from "../../../../contexts/GamesContext";
import { useAlert } from "../../../alerts/AlertsContext";
import LineupCardApi from "../../../../httpClients/LineupCardApi";
import useGameLogos from "../../../../customhooks/useGameLogos";
import { formatDate } from "../../../../utils/DateUtils";

const Section = styled.div`
  margin-bottom: 16px;
`;

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
`;

const FooterRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;

const PreviewWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PreviewImage = styled.img`
  max-height: 60px;
  max-width: 160px;
  object-fit: contain;
  border: 1px solid #bccadc;
  border-radius: 4px;
  padding: 4px;
`;

const RemoveIcon = styled(Icon)`
  cursor: pointer;
  color: #999;

  &:hover {
    color: #cc0000;
  }
`;

const LOGO_TYPES = [
  { key: "top", label: "Top Logo" },
  { key: "bottom", label: "Bottom Logo" },
  { key: "team", label: "Team Logo" },
];

const buildFileName = (orgLabel, type) => `${orgLabel}_${type}_custom`;

const LogoUploadPanel = ({ open, onClose, onSaved }) => {
  const {
    state: { selectedOrg, selectedGames },
  } = useGames();
  const showAlert = useAlert();

  const [files, setFiles] = useState({ top: null, bottom: null, team: null });
  const selectedGame = selectedGames?.[0];

  const { topLogo, bottomLogo, teamLogo } = useGameLogos(
    open ? { clubId: selectedOrg?.value, gamePk: selectedGame?.gamePk } : {},
  );

  useEffect(() => {
    if (!open) return;
    setFiles({ top: topLogo || null, bottom: bottomLogo || null, team: teamLogo || null });
  }, [open, topLogo, bottomLogo, teamLogo]);

  const handleFileAdd = (type, fileList) => {
    if (!fileList.length) return;
    const file = fileList[0];
    const reader = new FileReader();
    reader.onload = () => {
      setFiles((prev) => ({ ...prev, [type]: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleFileDelete = (type) => {
    setFiles((prev) => ({ ...prev, [type]: null }));
  };

  const hasAnyFile = Object.values(files).some(Boolean);
  const canSave = hasAnyFile && selectedOrg?.value && selectedGame?.gameDate;

  const handleSave = async () => {
    const gameDate = formatDate(selectedGame.gameDate);
    const gameType = selectedGame.gameType;
    const club = { id: selectedOrg.value, abbr: selectedOrg.label, name: selectedOrg.name };

    const uploads = LOGO_TYPES.filter(({ key }) => files[key]).map(({ key }) => {
      const logoRequest = {
        clubList: [club],
        fileBase64String: files[key],
        name: buildFileName(selectedOrg.label, key),
        type: key,
        subType: gameType,
        startDate: gameDate,
      };
      return LineupCardApi.saveLogoRequest(logoRequest);
    });

    try {
      await Promise.all(uploads);
      showAlert("Successfully saved logo(s)", "success");
      if (onSaved) onSaved();
      onClose();
    } catch {
      showAlert("Error saving logo(s)", "danger");
    }
  };

  return (
    <div>
      {LOGO_TYPES.map(({ key, label }) => (
        <Section key={key}>
          <SectionLabel>{label}</SectionLabel>
          {files[key] ? (
            <PreviewWrapper>
              <PreviewImage src={files[key]} alt={label} />
              <RemoveIcon iconName="fa-times-circle" onClick={() => handleFileDelete(key)} />
            </PreviewWrapper>
          ) : (
            <BrowseFiles
              editable={true}
              onFileAdd={(fileList) => handleFileAdd(key, fileList)}
              onFileDelete={() => handleFileDelete(key)}
              fileTypes={["image/png", "image/jpg", "image/jpeg"]}
            />
          )}
        </Section>
      ))}
      {/* TODO: Add checkbox to apply to all games in series */}
      <FooterRow>
        <ButtonRow>
          <PrimaryButton onClick={handleSave} disabled={!canSave}>
            Save
          </PrimaryButton>
          <LightButton onClick={onClose}>Cancel</LightButton>
        </ButtonRow>
      </FooterRow>
    </div>
  );
};

export default LogoUploadPanel;
